import React, { useEffect, useRef, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { InputNumber } from "primereact/inputnumber";
import { Tooltip } from "primereact/tooltip";
import { Chips } from "primereact/chips";
import { Divider } from "primereact/divider";
import CountryDropdown from "~/components/countryDropDown";
import { DatePicker } from "./DatePicker";
import { useVerifyDomains } from "~/api/tenants/hooks/useVerifyDomains";
import type { TenantInfoFormData } from "../../utils/formatters";
import { tenantOnboardingActions } from "../../store/tenantOnboardingStore";
import { Skeleton } from "primereact/skeleton";
import { Chip } from "primereact/chip";

interface TenantInfoFormProps {
  data: TenantInfoFormData;
  onChange: (updates: Partial<TenantInfoFormData>) => void;
  onBlur?: (fieldName: string) => void;
  errors: Record<string, string[]>;
  touchedFields?: Set<string>;
  disabled?: boolean;
  setFieldError?: (fieldName: string, messages: string[]) => void;
  clearFieldError?: (fieldName: string) => void;
  markFieldTouched?: (fieldName: string) => void;
  isEditing?: boolean;
  generalInformation?: TenantInfoFormData | null;
  loading?: boolean;
}

type DomainStatus =
  | { status: "checking" }
  | { status: "available" }
  | { status: "unavailable"; message: string }
  | { status: "invalid"; message: string }
  | { status: "error"; message: string };

const tenantTypeOptions = [
  { label: "Customer", value: "CUSTOMER" },
  { label: "Local Team", value: "LSP" },
];

const tenantStatusOptions = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];

/**
 * Step 1: General Information Form
 */
export const TenantInfoForm: React.FC<TenantInfoFormProps> = ({
  data,
  onChange,
  onBlur,
  errors,
  touchedFields = new Set(),
  disabled = false,
  setFieldError,
  clearFieldError,
  markFieldTouched,
  isEditing = false,
  generalInformation = null,
  loading = false,
}) => {
  const [domainStatuses, setDomainStatuses] = useState<
    Record<string, DomainStatus>
  >({});
  const [checkingDomain, setCheckingDomain] = useState<string | null>(null);
  const verifyDomainsMutation = useVerifyDomains();
  const previousDomainsRef = useRef<string[]>(data.domains || []);

  const handleFieldChange = (field: keyof TenantInfoFormData, value: any) => {
    onChange({ [field]: value });
  };
  useEffect(() => {
    tenantOnboardingActions.setDomainStatus(domainStatuses);
  }, [domainStatuses]);
  const handleFieldBlur = (field: keyof TenantInfoFormData) => {
    if (onBlur) {
      onBlur(field);
    }
  };

  // Only show errors for touched fields
  const getFieldError = (fieldName: string) => {
    if (touchedFields.has(fieldName) && errors[fieldName]) {
      return errors[fieldName];
    }
    return undefined;
  };

  const validateDomainLocally = (
    domain: string,
    allDomains: string[],
  ): string | null => {
    const trimmed = domain.trim();
    const domainRegex =
      /^(?=.{1,253}\.?$)(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,}\.?$/;

    if (trimmed.length < 3) {
      return "Domain must be at least 3 characters long";
    }
    if (trimmed.length > 253) {
      return "Domain must be at most 253 characters long";
    }
    // Check for duplicates (case-insensitive)
    const duplicates = allDomains.filter(
      (d) => d.trim().toLowerCase() === trimmed.toLowerCase(),
    );
    if (duplicates.length > 1) {
      return "Domain is duplicated in the list";
    }
    if (!domainRegex.test(trimmed)) {
      return "Domain has an invalid format";
    }
    return null;
  };

  const syncFieldError = (messages?: string[]) => {
    if (messages && messages.length > 0) {
      markFieldTouched?.("domains");
      setFieldError?.("domains", messages);
    } else {
      clearFieldError?.("domains");
    }
  };

  const updateDomainStatus = (domainKey: string, status: DomainStatus) => {
    setDomainStatuses((prev) => ({
      ...prev,
      [domainKey]: status,
    }));
  };

  const runAvailabilityCheck = async (domain: string, allDomains: string[]) => {
    const trimmed = domain.trim();
    if (!trimmed) return;

    const validationMessage = validateDomainLocally(trimmed, allDomains);
    if (validationMessage) {
      updateDomainStatus(trimmed, {
        status: "invalid",
        message: validationMessage,
      });
      syncFieldError([validationMessage]);
      return;
    }

    updateDomainStatus(trimmed, { status: "checking" });
    setCheckingDomain(trimmed);

    try {
      const response = await verifyDomainsMutation.mutateAsync([trimmed]);
      if (response.success && response.data) {
        const result = response.data.results.find(
          (item) => item.domain.toLowerCase() === trimmed.toLowerCase(),
        );

        if (result && !result.available) {
          const message = `Domain is already in use by ${result.tenant_name || "another tenant"}`;
          updateDomainStatus(trimmed, {
            status: "unavailable",
            message,
          });
          syncFieldError([message]);
        } else {
          updateDomainStatus(trimmed, { status: "available" });
        }
      } else {
        updateDomainStatus(trimmed, {
          status: "error",
          message: "Unable to verify domain right now.",
        });
        syncFieldError(["Unable to verify domain right now."]);
      }
    } catch (error) {
      console.error("Domain availability check failed", error);
      updateDomainStatus(trimmed, {
        status: "error",
        message: "Unable to verify domain right now.",
      });
      syncFieldError(["Unable to verify domain right now."]);
    } finally {
      setCheckingDomain(null);
    }
  };

  useEffect(() => {
    let currentDomains = data.domains || [];
    if (isEditing) {
      currentDomains = currentDomains.filter((data) => {
        return !generalInformation?.domains.includes(data);
      });
    }

    const previousDomains = previousDomainsRef.current;

    // Remove statuses for deleted domains
    const removedDomains = previousDomains.filter(
      (domain) => !currentDomains.includes(domain),
    );
    if (removedDomains.length > 0) {
      setDomainStatuses((prev) => {
        const updated = { ...prev };
        removedDomains.forEach((domain) => {
          delete updated[domain.trim()];
        });
        return updated;
      });
    }

    // Check any newly added domains
    const newDomains = currentDomains.filter(
      (domain) => !previousDomains.includes(domain),
    );
    newDomains.forEach((domain) =>
      runAvailabilityCheck(domain, currentDomains),
    );

    previousDomainsRef.current = currentDomains;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.domains]);

  useEffect(() => {
    const hasBlockingDomainIssue = Object.values(domainStatuses).some(
      (status) =>
        status.status === "invalid" ||
        status.status === "unavailable" ||
        status.status === "error",
    );

    // Only clear errors if there are no blocking issues AND no validation errors
    // This preserves duplicate errors from validateStep1
    if (!hasBlockingDomainIssue) {
      // Check if there are validation errors (like duplicates) from the errors prop
      const validationErrors = errors.domains;
      // Only clear if there are no validation errors (like duplicates)
      if (!validationErrors || validationErrors.length === 0) {
        clearFieldError?.("domains");
      }
    }
  }, [domainStatuses, clearFieldError, errors]);

  return (
    <div className="formgrid grid">
      <div className="field col-12">
        {!isEditing && <p className="heading">General Information</p>}
      </div>

      {/* Tenant Details Section */}
      <div className="field col-12">
        <h3 className="m-0 mb-3">Tenant Details</h3>
      </div>

      <div className="field col-12 md:col-6">
        <label htmlFor="tenant_name" className="input-label mb-2">
          Tenant Name *{" "}
          <span id="tenant-name-info" className="pi pi-info-circle" />
          <Tooltip
            target="#tenant-name-info"
            content="The full legal name of the tenant."
            position="right"
            className="custom-tooltip tooltip-custom-format"
          />
        </label>
        {loading ? (
          <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
        ) : (
          <InputText
            id="tenant_name"
            name="tenant_name"
            value={data.tenant_name}
            onChange={(e) => handleFieldChange("tenant_name", e.target.value)}
            onBlur={() => handleFieldBlur("tenant_name")}
            className={`w-full input-field  ${getFieldError("tenant_name") ? "p-invalid" : ""}`}
            disabled={disabled}
            placeholder="Enter the tenant's legal name"
            autoComplete="organization"
            tabIndex={1}
            aria-describedby={
              getFieldError("tenant_name")
                ? "tenant_name-error"
                : "tenant-name-info"
            }
            aria-invalid={!!getFieldError("tenant_name")}
            aria-required="true"
          />
        )}
        {getFieldError("tenant_name") && (
          <small id="tenant_name-error" className="p-error" role="alert">
            {getFieldError("tenant_name")[0]}
          </small>
        )}
      </div>

      <div className="field col-12 md:col-6 hidden md:block" />

      <div className="field col-12 md:col-6">
        <label htmlFor="type" className="input-label mb-2">
          Type *
        </label>
        {loading ? (
          <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
        ) : (
          <Dropdown
            id="type"
            name="type"
            value={data.type || null}
            onChange={(e) => {
              handleFieldChange("type", e.value);
              // Clear Local Team specific fields if type changes from LSP
              if (e.value !== "LSP") {
                handleFieldChange("supports_payroll_plus", null);
                handleFieldChange("payslip_portal_link", "");
              }
            }}
            onBlur={() => handleFieldBlur("type")}
            options={tenantTypeOptions}
            optionLabel="label"
            optionValue="value"
            className={`w-full dropdown-field ${getFieldError("type") ? "p-invalid" : ""}`}
            disabled={disabled}
            placeholder="Select tenant type"
            tabIndex={2}
            aria-describedby={getFieldError("type") ? "type-error" : undefined}
            aria-invalid={!!getFieldError("type")}
            aria-required="true"
          />
        )}
        {getFieldError("type") && (
          <small id="type-error" className="p-error" role="alert">
            {getFieldError("type")[0]}
          </small>
        )}
      </div>

      <div className="field col-12 md:col-6">
        <label htmlFor="demo_tenant" className="input-label mb-2 opacity-0">
          Demo Tenant
        </label>
        <div className="flex align-items-center mt-1 ml-1 pt-2 pl-2">
          <Checkbox
            inputId="demo_tenant"
            checked={data.demo_tenant}
            onChange={(e) => handleFieldChange("demo_tenant", e.checked)}
            disabled={disabled}
          />
          <label htmlFor="demo_tenant" className="ml-2 ">
            Demo Tenant
          </label>
        </div>
      </div>

      <div className="field col-12 domains-chip-container">
        <label className="input-label mb-2" htmlFor="domains">
          Domain(s) * <span id="domain-info" className="pi pi-info-circle" />
          <Tooltip
            target="#domain-info"
            content="Used to automatically detect and assign users to this tenant. Separate multiple domains with a comma or press enter to add a new domain."
            position="right"
            className="custom-tooltip tooltip-custom-format"
          />
        </label>
        {loading ? (
          <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
        ) : !disabled ? (
          <Chips
            id="domains"
            name="domains"
            contentEditable
            value={data.domains || []}
            onChange={(e) => handleFieldChange("domains", e.value || [])}
            onBlur={() => handleFieldBlur("domains")}
            separator=","
            placeholder="Enter tenant domain"
            className={`w-full ${getFieldError("domains") && getFieldError("domains")!.length > 0 ? "p-invalid" : ""}`}
            disabled={disabled}
            tabIndex={3}
            aria-describedby={
              getFieldError("domains") ? "domains-error" : "domain-info"
            }
            aria-invalid={!!getFieldError("domains")}
            aria-required="true"
          />
        ) : (
          <div className="domain-chip-data">
            {" "}
            {data.domains?.map((data) => {
              return (
                <Chip className="mr-1" aria-disabled={true} label={data} />
              );
            })}
          </div>
        )}
        <div className="mt-2 flex flex-column gap-1">
          {Object.entries(domainStatuses).map(([domain, status]) => {
            if (status.status === "checking") {
              return (
                <div
                  key={domain}
                  className="flex align-items-center gap-2 text-500"
                >
                  <i
                    className="pi pi-spin pi-spinner text-primary"
                    aria-hidden
                  />
                  <small>{`Checking ${domain} availability...`}</small>
                </div>
              );
            }
            if (status.status === "available") {
              return (
                <div
                  key={domain}
                  className="flex align-items-center gap-2 text-green-600"
                >
                  <i className="pi pi-check-circle" aria-hidden />
                  <small>{`${domain} is available`}</small>
                </div>
              );
            }
            const errorMessage = status.message || "Unable to verify domain";
            return (
              <div
                key={domain}
                className="flex align-items-center gap-2 text-red-600"
              >
                <i className="pi pi-times-circle" aria-hidden />
                <small>{`${domain}: ${errorMessage}`}</small>
              </div>
            );
          })}
        </div>
        {getFieldError("domains") && getFieldError("domains")!.length > 0 && (
          <small id="domains-error" className="p-error" role="alert">
            {getFieldError("domains")![0]}
          </small>
        )}
      </div>

      {/* Local Team Tenant Details Section - Only show when type is LSP */}
      {data.type === "LSP" && (
        <>
          <div className="field col-12">
            <Divider />
          </div>

          <div className="field col-12">
            <h3 className="m-0 mb-3">Local Team Tenant Details</h3>
          </div>

          <div className="field col-12 md:col-6">
            <label htmlFor="supports_payroll_plus" className="input-label mb-2">
              Does this Local Team support Payroll +? *
            </label>
            <Dropdown
              id="supports_payroll_plus"
              name="supports_payroll_plus"
              value={data.supports_payroll_plus}
              onChange={(e) => {
                handleFieldChange("supports_payroll_plus", e.value);
                // Clear payslip portal link if "No" is selected
                if (e.value === false) {
                  handleFieldChange("payslip_portal_link", "");
                }
              }}
              onBlur={() => handleFieldBlur("supports_payroll_plus")}
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ]}
              optionLabel="label"
              optionValue="value"
              className={`w-full dropdown-field ${getFieldError("supports_payroll_plus") ? "p-invalid" : ""}`}
              disabled={disabled}
              placeholder="Select an option"
              tabIndex={11}
              aria-describedby={
                getFieldError("supports_payroll_plus")
                  ? "supports_payroll_plus-error"
                  : undefined
              }
              aria-invalid={!!getFieldError("supports_payroll_plus")}
              aria-required="true"
            />
            {getFieldError("supports_payroll_plus") && (
              <small
                id="supports_payroll_plus-error"
                className="p-error"
                role="alert"
              >
                {getFieldError("supports_payroll_plus")[0]}
              </small>
            )}
          </div>

          <div className="field col-12 md:col-6 hidden md:block" />

          <div className="field col-12">
            <label htmlFor="payslip_portal_link" className="input-label mb-2">
              Payslip Portal Link (Local Team){" "}
              <span id="payslip-portal-info" className="pi pi-info-circle" />
              <Tooltip
                target="#payslip-portal-info"
                content="The Local Team's dedicated payslip portal link defines where users will be redirected when clicking 'Open Payslip'."
                position="right"
                className="custom-tooltip tooltip-custom-format"
              />
            </label>
            <InputText
              id="payslip_portal_link"
              name="payslip_portal_link"
              value={data.payslip_portal_link}
              onChange={(e) =>
                handleFieldChange("payslip_portal_link", e.target.value)
              }
              onBlur={() => handleFieldBlur("payslip_portal_link")}
              className={`w-full input-field ${getFieldError("payslip_portal_link") ? "p-invalid" : ""}`}
              disabled={disabled || data.supports_payroll_plus !== true}
              placeholder="Enter Payslip URL"
              type="url"
              autoComplete="url"
              tabIndex={12}
              aria-describedby={
                getFieldError("payslip_portal_link")
                  ? "payslip_portal_link-error"
                  : "payslip-portal-info"
              }
              aria-invalid={!!getFieldError("payslip_portal_link")}
            />
            <div className="flex flex-column gap-1 mt-1">
              <small
                className="text-color-secondary"
                style={{ fontSize: "0.875rem", display: "block" }}
              >
                This link will replace the standard Payslip button destination
                for customers serviced by this Local Team.
              </small>
              {getFieldError("payslip_portal_link") && (
                <small
                  id="payslip_portal_link-error"
                  className="p-error"
                  role="alert"
                  style={{ display: "block" }}
                >
                  {getFieldError("payslip_portal_link")[0]}
                </small>
              )}
            </div>
          </div>
        </>
      )}

      <div className="field col-12">
        <Divider />
      </div>

      {/* Account Details Section */}
      <div className="field col-12">
        <h3 className="m-0 mb-3">Account Details</h3>
      </div>

      <div className="field col-12 md:col-6">
        <label htmlFor="account_name" className="input-label mb-2">
          Account Name *{" "}
          <span id="account-name-info" className="pi pi-info-circle" />
          <Tooltip
            target="#account-name-info"
            content="The commercial or billing name associated with the tenant."
            position="right"
            className="custom-tooltip tooltip-custom-format"
          />
        </label>
        {loading ? (
          <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
        ) : (
          <InputText
            id="account_name"
            name="account_name"
            value={data.account_name}
            onChange={(e) => handleFieldChange("account_name", e.target.value)}
            onBlur={() => handleFieldBlur("account_name")}
            className={`w-full input-field ${getFieldError("account_name") ? "p-invalid" : ""}`}
            disabled={disabled}
            placeholder="Enter the account's name"
            autoComplete="organization"
            tabIndex={4}
            aria-describedby={
              getFieldError("account_name")
                ? "account_name-error"
                : "account-name-info"
            }
            aria-invalid={!!getFieldError("account_name")}
            aria-required="true"
          />
        )}
        {getFieldError("account_name") && (
          <small id="account_name-error" className="p-error" role="alert">
            {getFieldError("account_name")[0]}
          </small>
        )}
      </div>

      <div className="field col-12 md:col-6">
        <label htmlFor="salesforce_id" className="input-label mb-2">
          Salesforce ID
        </label>
        {loading ? (
          <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
        ) : (
          <InputText
            id="salesforce_id"
            name="salesforce_id"
            value={data.salesforce_id}
            onChange={(e) => handleFieldChange("salesforce_id", e.target.value)}
            onBlur={() => handleFieldBlur("salesforce_id")}
            className={`w-full input-field ${getFieldError("salesforce_id") ? "p-invalid" : ""}`}
            disabled={disabled}
            placeholder="Enter Salesforce ID"
            maxLength={20}
            autoComplete="off"
            tabIndex={5}
            aria-describedby={
              getFieldError("salesforce_id") ? "salesforce_id-error" : undefined
            }
            aria-invalid={!!getFieldError("salesforce_id")}
          />
        )}
        {getFieldError("salesforce_id") && (
          <small id="salesforce_id-error" className="p-error" role="alert">
            {getFieldError("salesforce_id")[0]}
          </small>
        )}
      </div>

      <div className="field col-12 md:col-6">
        <label htmlFor="country" className="input-label mb-2">
          Country *
        </label>
        <CountryDropdown
          id="country"
          name="country"
          className="dropdown-field"
          value={data.country}
          onChange={(e) => {
            const twoCharCode = e.value?.code || "";
            handleFieldChange("country", twoCharCode);
          }}
          onBlur={() => handleFieldBlur("country")}
          error={
            getFieldError("country")
              ? { message: getFieldError("country")![0] }
              : undefined
          }
          disabled={disabled}
          tabIndex={6}
          aria-describedby={
            getFieldError("country") ? "country-error" : undefined
          }
          aria-required="true"
        />
        {getFieldError("country") && (
          <small id="country-error" className="p-error" role="alert">
            {getFieldError("country")[0]}
          </small>
        )}
      </div>

      <div className="field col-12 md:col-6">
        <label htmlFor="total_number_of_users" className="input-label mb-2">
          Total Number of Users *{" "}
          <span id="users-info" className="pi pi-info-circle" />
          <Tooltip
            target="#users-info"
            content="Expected number of users who will access this tenant."
            position="right"
            className="custom-tooltip tooltip-custom-format"
          />
        </label>
        {loading ? (
          <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
        ) : (
          <InputNumber
            id="total_number_of_users"
            name="total_number_of_users"
            value={data.total_number_of_users ?? null}
            onValueChange={(e) =>
              handleFieldChange("total_number_of_users", e.value ?? null)
            }
            onBlur={() => {
              // Add small delay to ensure state is updated before validation
              setTimeout(() => {
                handleFieldBlur("total_number_of_users");
              }, 200);
            }}
            className={`w-full ${getFieldError("total_number_of_users") ? "p-invalid" : ""}`}
            disabled={disabled}
            min={1}
            max={9999}
            useGrouping={false}
            placeholder="Enter number of users"
            tabIndex={7}
            aria-describedby={
              getFieldError("total_number_of_users")
                ? "total_number_of_users-error"
                : "users-info"
            }
            aria-invalid={!!getFieldError("total_number_of_users")}
            aria-required="true"
          />
        )}
        {getFieldError("total_number_of_users") && (
          <small
            id="total_number_of_users-error"
            className="p-error"
            role="alert"
          >
            {getFieldError("total_number_of_users")[0]}
          </small>
        )}
      </div>

      <div className="field col-12 md:col-6">
        <label htmlFor="status" className="input-label mb-2">
          Status *
        </label>
        {loading ? (
          <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
        ) : (
          <Dropdown
            id="status"
            name="status"
            value={data.status || null}
            onChange={(e) => handleFieldChange("status", e.value)}
            onBlur={() => handleFieldBlur("status")}
            options={tenantStatusOptions}
            optionLabel="label"
            optionValue="value"
            className={`w-full dropdown-field ${getFieldError("status") ? "p-invalid" : ""}`}
            disabled={disabled}
            placeholder="Select status"
            tabIndex={8}
            aria-describedby={
              getFieldError("status") ? "status-error" : undefined
            }
            aria-invalid={!!getFieldError("status")}
            aria-required="true"
          />
        )}
        {getFieldError("status") && (
          <small id="status-error" className="p-error" role="alert">
            {getFieldError("status")[0]}
          </small>
        )}
      </div>

      <div className="field col-12">
        <Divider />
      </div>

      {/* Billing Details Section */}
      <div className="field col-12">
        <h3 className="m-0 mb-3">Billing Details</h3>
      </div>

      <div className="field col-12 md:col-6">
        {loading ? (
          <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
        ) : (
          <DatePicker
            id="billing_start_date"
            name="billing_start_date"
            value={data.billing_start_date}
            onChange={(date) => handleFieldChange("billing_start_date", date)}
            onBlur={() => handleFieldBlur("billing_start_date")}
            label="Start Date"
            required
            errors={getFieldError("billing_start_date")}
            disabled={disabled}
            tooltipContent="Date when the billing period begins."
            tooltipId="billing-start-date-info"
            placeholder="Select Start Date"
            tabIndex={9}
          />
        )}
      </div>

      <div className="field col-12 md:col-6">
        {loading ? (
          <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
        ) : (
          <DatePicker
            id="billing_end_date"
            name="billing_end_date"
            value={data.billing_end_date}
            onChange={(date) => handleFieldChange("billing_end_date", date)}
            onBlur={() => handleFieldBlur("billing_end_date")}
            label="End Date"
            required={false}
            errors={getFieldError("billing_end_date")}
            disabled={disabled}
            minDate={data.billing_start_date || undefined}
            tooltipContent="Date when the billing period ends (optional for ongoing contracts)."
            tooltipId="billing-end-date-info"
            placeholder="Select End Date"
            tabIndex={10}
          />
        )}
      </div>
    </div>
  );
};
